import { useState, useEffect } from 'react'
import api from '../../services/api.js'
import { useAuth } from '../../hooks/useAuth.js'
import InvoiceView from '../projects/InvoiceView.jsx'
import { formatDate } from '../../utils/helpers'
import { useTranslation } from 'react-i18next'

export default function InvoiceQueue() {
  const { t } = useTranslation('money')
  const { user } = useAuth()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [processing, setProcessing] = useState({})
  const [messages, setMessages] = useState({})
  const [viewingInvoice, setViewingInvoice] = useState(null) // milestone_id
  const [history, setHistory] = useState([])

  const loadQueue = () => {
    setLoading(true)
    api.get('/deliverables/invoices/pending')
      .then(r => setItems(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Failed to load invoice queue'))
      .finally(() => setLoading(false))
  }

  const loadHistory = () => {
    api.get('/deliverables/invoices/paid')
      .then(r => setHistory(r.data))
      .catch(() => {})
  }

  useEffect(() => { loadQueue(); loadHistory() }, [])

  const recordPayment = async (milestoneId) => {
    // Final action — confirm before paying & approving.
    if (!window.confirm('Pay & Approve this invoice? This is final — the invoice will be paid and moved to history.')) return
    setProcessing(p => ({ ...p, [milestoneId]: true }))
    setMessages(m => ({ ...m, [milestoneId]: null }))
    try {
      await api.post(`/deliverables/invoices/${milestoneId}/record-payment`, {})
      setMessages(m => ({ ...m, [milestoneId]: { type: 'success', text: 'Paid & approved. Consultant and PM have been notified — moved to history.' } }))
      loadQueue()
      loadHistory()
    } catch (e) {
      setMessages(m => ({
        ...m,
        [milestoneId]: { type: 'error', text: e.response?.data?.detail || 'Failed to record payment' },
      }))
    } finally {
      setProcessing(p => ({ ...p, [milestoneId]: false }))
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">{error}</div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('invoice_payment_queue')}</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Invoices confirmed by the consultant and approved by both Admin and PM — ready for payment.
        </p>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-5xl mb-4">✓</div>
          <p className="text-sm font-medium">{t('no_invoices_awaiting')}</p>
          <p className="text-xs mt-1">Invoices appear here only after consultant confirmation + Admin + PM approval.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map(item => (
            <div key={item.milestone_id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{item.milestone_title}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Project: <span className="text-gray-700">{item.project_name || `#${item.project_id}`}</span>
                  </p>
                  {item.consultant_confirmed_at && (
                    <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                      Consultant confirmed: {formatDate(item.consultant_confirmed_at)}
                    </p>
                  )}
                  {item.invoice_triggered_at && (
                    <p className="text-xs text-gray-400 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
                      Invoice raised: {formatDate(item.invoice_triggered_at)}
                    </p>
                  )}
                </div>

                <div className="flex flex-col items-end gap-2 shrink-0">
                  <div className="text-xl font-bold text-gray-900">
                    {item.payment_amount ? `$${Number(item.payment_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                  </div>

                  {/* View Invoice button */}
                  <button
                    onClick={() => setViewingInvoice(item.milestone_id)}
                    className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                  >
                    🧾 View Invoice
                  </button>

                  {/* Pay & Approve — final action */}
                  <button
                    onClick={() => recordPayment(item.milestone_id)}
                    disabled={!!processing[item.milestone_id]}
                    className="px-4 py-1.5 text-sm bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 w-full text-center"
                  >
                    {processing[item.milestone_id] ? 'Processing...' : '💸 Pay & Approve'}
                  </button>
                </div>
              </div>

              {messages[item.milestone_id] && (
                <div className={`mt-3 text-sm px-3 py-2 rounded-lg ${
                  messages[item.milestone_id].type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {messages[item.milestone_id].text}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Payment history — archive of paid & approved invoices */}
      {history.length > 0 && (
        <div className="mt-10">
          <h2 className="text-lg font-bold text-gray-800">{t('payment_history')}</h2>
          <p className="text-sm text-gray-500 mt-0.5 mb-4">{t('already_paid_archived')}</p>
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {history.map(h => (
              <div key={h.milestone_id} className="flex items-center justify-between gap-4 px-5 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{h.milestone_title}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {h.project_name || `#${h.project_id}`}
                    {h.project_number ? ` · ${h.project_number}` : ''}
                    {h.paid_at ? ` · ${formatDate(h.paid_at)}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-sm font-semibold text-gray-900">
                    {h.payment_amount ? `$${Number(h.payment_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                  </span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Paid</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invoice detail modal */}
      {viewingInvoice && (
        <InvoiceView
          milestoneId={viewingInvoice}
          onClose={() => setViewingInvoice(null)}
        />
      )}
    </div>
  )
}
